import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter } from 'rxjs';
import { Question } from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';
import { CameraService } from '../document-kyc-upload-modal/services/camera.service';
import { DocumentUploadModalComponent } from '../document-upload-modal/document-upload-modal.component';
import { QuestionContext } from '../question-multi-file/question-multi-file.component';

@Component({
  selector: 'app-question-camera-file',
  templateUrl: './question-camera-file.component.html',
  styleUrls: ['./question-camera-file.component.scss'],
})
export class QuestionCameraFileComponent implements OnInit, OnDestroy {
  @Input() question!: Question;
  @Input() context!: QuestionContext;
  @Output() changeAnswer: EventEmitter<QuestionAnswer> = new EventEmitter();

  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;

  // Camera properties
  isCameraActive = false;
  capturedPhoto: string | null = null;
  isLoading = false;
  facingMode: 'user' | 'environment' = 'environment';
  hasMultipleCameras = false;

  // File upload properties
  uploadedFile: string | null = null;
  fileName: string = '';

  private readonly allowedDocumentTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    'application/rtf',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'application/xml',
    'text/xml',
    'application/json',
  ];

  private readonly allowedImageTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/svg+xml',
    'image/avif',
  ];

  private readonly acceptedExtensions = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.txt',
    '.csv',
    '.rtf',
    '.odt',
    '.ods',
    '.odp',
    '.xml',
    '.json',
    '.jpg',
    '.jpeg',
    '.jpe',
    '.jfif',
    '.png',
    '.gif',
    '.bmp',
    '.tif',
    '.tiff',
    '.webp',
    '.heic',
    '.heif',
    '.svg',
    '.avif',
  ];

  private readonly acceptedFormatsLabel =
    'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, RTF, ODT, ODS, ODP, XML, JSON, JPG, JPEG, PNG, GIF, BMP, TIFF, WEBP, HEIC, HEIF, SVG, AVIF';

  constructor(
    private cameraService: CameraService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.checkCameraAvailability();
    
    // Se já existe uma resposta, determinar se é foto ou arquivo
    if (this.question.answer?.value) {
      const value = this.question.answer.value;
      if (typeof value === 'string') {
        // Se começar com data:image, é uma foto capturada
        if (value.startsWith('data:image')) {
          this.capturedPhoto = value;
        }
      } else if (Array.isArray(value) && value.length > 0) {
        // É um arquivo enviado (array de URLs)
        this.uploadedFile = value[0];
        this.fileName = this.extractFileName(value[0]);
      }
    }
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  async checkCameraAvailability(): Promise<void> {
    const capabilities = await this.cameraService.checkCameraAvailability();
    this.hasMultipleCameras = capabilities.cameras.length > 1;
  }

  // Camera methods
  async startCamera(): Promise<void> {
    try {
      this.isLoading = true;
      this.isCameraActive = true;
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const stream = await this.cameraService.startCamera(this.facingMode);
      
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = stream;
        console.log('Camera stream set successfully');
      } else {
        console.error('Video element not found');
        this.isCameraActive = false;
      }
    } catch (error: any) {
      console.error('Error starting camera:', error);
      this.isCameraActive = false;
      this.snackBar.open(
        error.message || 'Failed to start camera. Please check permissions.',
        'Close',
        { duration: 5000 }
      );
    } finally {
      this.isLoading = false;
    }
  }

  stopCamera(): void {
    this.cameraService.stopCamera();
    this.isCameraActive = false;
  }

  async capturePhoto(): Promise<void> {
    try {
      if (!this.videoElement || !this.videoElement.nativeElement) {
        throw new Error('Video element not found');
      }

      this.isLoading = true;
      const blob = await this.cameraService.capturePhoto(
        this.videoElement.nativeElement,
        0.95
      );

      const reader = new FileReader();
      reader.onloadend = () => {
        this.capturedPhoto = reader.result as string;
        this.uploadedFile = null; // Limpar arquivo se existir
        this.fileName = '';
        this.stopCamera();
        this.emitAnswer(this.capturedPhoto);
      };
      reader.readAsDataURL(blob);
    } catch (error: any) {
      console.error('Error capturing photo:', error);
      this.snackBar.open(
        'Failed to capture photo. Please try again.',
        'Close',
        { duration: 3000 }
      );
    } finally {
      this.isLoading = false;
    }
  }

  async switchCamera(): Promise<void> {
    try {
      this.isLoading = true;
      const stream = await this.cameraService.switchCamera(this.facingMode);
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = stream;
        console.log('Camera switched successfully');
      }
    } catch (error: any) {
      console.error('Error switching camera:', error);
      this.snackBar.open(
        'Failed to switch camera.',
        'Close',
        { duration: 3000 }
      );
    } finally {
      this.isLoading = false;
    }
  }

  retakePhoto(): void {
    this.capturedPhoto = null;
    this.startCamera();
  }

  removePhoto(): void {
    this.capturedPhoto = null;
    this.emitAnswer(null);
  }

  // File upload methods
  openUploadModal(): void {
    const dialogData = {
      bucket: this.question.configuration?.bucket || 'default',
      name: this.question.slug,
      multiple: false, // Apenas 1 arquivo
      ref: this.context.claim_id!,
      question_id: this.question.id,
      type: 'CLAIM' as const,
      maxFileSize: this.question.configuration?.maxFileSize,
      maxFiles: 1,
      allowedTypes: [...this.allowedDocumentTypes, ...this.allowedImageTypes],
      accept: this.acceptedExtensions.join(','),
      acceptedFormatsLabel: this.acceptedFormatsLabel,
    };

    const dialogRef = this.dialog.open(DocumentUploadModalComponent, {
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(filter((e) => !!e))
      .subscribe((result) => {
        const parsed = JSON.parse(result);
        if (parsed.status === 'COMPLETED' && parsed.value && parsed.value.length > 0) {
          this.uploadedFile = parsed.value[0];
          this.fileName = this.extractFileName(parsed.value[0]);
          this.capturedPhoto = null; // Limpar foto se existir
          this.emitAnswer(parsed.value);
        }
      });
  }

  removeFile(): void {
    this.uploadedFile = null;
    this.fileName = '';
    this.emitAnswer(null);
  }

  private extractFileName(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split('/');
      return parts[parts.length - 1] || 'arquivo';
    } catch {
      return 'arquivo';
    }
  }

  private emitAnswer(value: string | string[] | null): void {
    const answer: QuestionAnswer = {
      question: this.question,
      answer: {
        value: value as any,
        isIgnored: false,
      },
    };
    this.changeAnswer.emit(answer);
  }
}
